import { Fragment, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { cx } from '@/utils';
import DotButton, { useDotButton } from '@common/DotButton/DotButton';
import SvgArrow from '@common/Icons/Arrow';
import { Button } from '@material-tailwind/react';
import { ROUTES_CLIENT } from '@utils/constants';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import useEmblaCarousel from 'embla-carousel-react';

import CustomLink from '@/components/common/CustomLink';
import FacebookIconX from '@/components/common/Icons/FacebookIcon';
import IconNoResults from '@/components/common/Icons/IconNoResults';
import InstagramIcon from '@/components/common/Icons/InstagramIcon';
import TwitterIconX from '@/components/common/Icons/TwitterIcon';
import {
  PageBlockStyles,
  PageCabinetBlock,
  RouterType,
  type CabinetDetail,
} from '@/components/common/Providers/GraphqlProvider/generated';
import SvgBgPrimary from '@/components/common/SvgBgPrimary/SvgBgPrimary';
import { Typography } from '@/components/ui/Typography';

import { TitleWrapper } from '../../../views/Home/components';
import { useCabinet } from './store/useCabinet';
import styles from './styles.module.css';

const Cabinet = ({ currentCabinetBlock }: { currentCabinetBlock?: PageCabinetBlock }) => {
  const {
    home: { cabinetBlock: cabinetBlockContext },
  } = useHomeContext();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: 1,
  });

  const cabinetBlock = currentCabinetBlock || cabinetBlockContext;

  const cabinetSorted: any = cabinetBlock?.items
    ? [...cabinetBlock?.items]?.sort((a: any, b: any) => a.orderIndex - b.orderIndex)
    : undefined;

  const isCarrousel = cabinetBlock?.isCarrousel ?? true;

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  const { addCurrentCabinet } = useCabinet();

  const [isLineClamped, setIsLineClamped] = useState(true);

  const router = useRouter();

  if (cabinetBlock?.isDisabled) return null;

  return (
    <>
      <div className={'inner-extend mb-7 mt-14'}>
        <div className="flex flex-col justify-between md:flex-row md:items-center md:justify-between">
          <div>
            <TitleWrapper
              title1={!cabinetBlock?.showTitle ? '' : cabinetBlock?.cabinetTitle}
              title2={!cabinetBlock?.showTitle ? '' : cabinetBlock?.cabinetSubtitle || ''}
              wrapperProps={{ style: { border: 'none' } }}
            />
          </div>
        </div>
      </div>
      <section className={' bg-concrete py-1'}>
        <div className={'inner-extend'}>
          {cabinetBlock?.showDescription && (
            <Typography variant="body1" className={'title-home2 mt-3  text-tundora'}>
              {cabinetBlock?.extendedDescription}
            </Typography>
          )}

          <div className={'relative mt-12'}>
            {!cabinetSorted?.length ? (
              <div className="flex h-[100px] w-full animate-fade-in items-center justify-center rounded-b-lg">
                <IconNoResults />
                <p>No hay personal actualmente</p>
              </div>
            ) : isCarrousel ? (
              <>
                <div>
                  <button
                    className={'absolute inset-y-0 -left-10 z-10 hidden rotate-180 md:block'}
                    onClick={() => emblaApi?.scrollPrev()}
                  >
                    <SvgArrow className="text-primary" />
                  </button>
                  <button
                    className={'absolute inset-y-0 -right-10 z-10 hidden md:block'}
                    onClick={() => emblaApi?.scrollNext()}
                  >
                    <SvgArrow className="text-primary" />
                  </button>
                </div>

                <div className="overflow-hidden pb-6 pt-4" ref={emblaRef}>
                  <div className="flex">
                    {cabinetSorted?.map((cabinet: any, key: any) => (
                      <div
                        key={key}
                        className="flex min-w-0 flex-[0_0_100%] justify-center px-4 sm:flex-[0_0_50%] md:flex-[0_0_33.333%] lg:flex-[0_0_25%] xl:flex-[0_0_20%]"
                      >
                        <CabinetCard
                          cabinet={cabinet}
                          cabinetBlock={cabinetBlock}
                          addCurrentCabinet={addCurrentCabinet}
                          router={router}
                          isLineClamped={isLineClamped}
                          setIsLineClamped={setIsLineClamped}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap justify-center pb-6 pt-4">
                {cabinetSorted?.map((cabinet: any, key: any) => (
                  <div
                    key={key}
                    className="mb-8 flex w-full justify-center px-4 sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/5"
                  >
                    <CabinetCard
                      cabinet={cabinet}
                      cabinetBlock={cabinetBlock}
                      addCurrentCabinet={addCurrentCabinet}
                      router={router}
                      isLineClamped={isLineClamped}
                      setIsLineClamped={setIsLineClamped}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          {isCarrousel && cabinetSorted?.length > 0 && (
            <div className={'dots  mt-10'}>
              {scrollSnaps.map((_, index) => (
                <DotButton
                  key={index}
                  onClick={() => onDotButtonClick(index)}
                  className={cx('dot', {
                    [styles.selected]: index === selectedIndex,
                  })}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Cabinet;

export function CabinetSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="inner-extend">
        <div className="mb-2 h-4 w-48 rounded bg-gray-200" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="h-40 w-full rounded bg-gray-200" />
          <div className="h-40 w-full rounded bg-gray-200" />
          <div className="h-40 w-full rounded bg-gray-200" />
        </div>
      </div>
    </div>
  );
}

function CabinetCard({
  cabinet,
  cabinetBlock,
  addCurrentCabinet,
  router,
  isLineClamped,
  setIsLineClamped,
}: any) {
  // Verifica si tiene un linkItem configurado con acción diferente a NoAction
  const hasCustomLink =
    cabinet?.linkItem?.routeType && cabinet.linkItem.routeType !== RouterType.NoAction;

  // Función de navegación que considera el linkItem configurado
  const handleNavigation = () => {
    if (hasCustomLink) {
      // Si tiene linkItem configurado, usar CustomLink maneja la navegación
      // pero para onClick directo necesitamos la URL
      return;
    }
    // Comportamiento por defecto: ir a cabinet/personal
    addCurrentCabinet(cabinet);
    router.push(`${ROUTES_CLIENT.CABINET}/personal`);
  };

  // Contenido de la imagen clickeable
  const imageContent = (
    <div className="absolute left-1/2 top-0 mt-[-15px] flex h-[150px] w-[150px] -translate-x-1/2 cursor-pointer items-center justify-center">
      <Image
        src={cabinet.cabinetImage?.downloadUrl || '/placeholder.png'}
        alt={'Banner'}
        width={150}
        height={150}
        className="size-[150px] rounded-full border-2 border-blueLight bg-white object-cover"
      />
    </div>
  );

  return (
    <div className="dark:bg-neutral-700 relative flex h-[350px] w-full max-w-[280px] flex-col justify-center rounded-lg border-2 border-blueLight bg-white shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)]">
      {hasCustomLink ? (
        <CustomLink pageLink={cabinet.linkItem} href="#">
          {imageContent}
        </CustomLink>
      ) : (
        <div onClick={handleNavigation}>{imageContent}</div>
      )}

      <div className="mt-[135px] flex w-full flex-col px-4 text-base font-bold text-primary">
        {/* Sección del contenido principal que crece */}
        <div className="flex-grow">
          <p className="mb-2 min-h-[35px] break-words text-center text-sm leading-tight">
            {cabinet?.cabinetName}
          </p>
          <h1
            onClick={() => {
              setIsLineClamped((prev: boolean) => !prev);
            }}
            className={cx(
              'mb-4 cursor-pointer text-center text-xs text-black',
              { 'line-clamp-3': isLineClamped },
              { 'line-clamp-none': !isLineClamped }
            )}
          >
            {cabinet.cabinetPosition?.name}
          </h1>
        </div>

        {/* Sección del botón - siempre en la misma posición */}
        {cabinetBlock?.pageBlockStyle === PageBlockStyles.B ? (
          <div className="mb-4 flex justify-center">
            {hasCustomLink ? (
              <CustomLink pageLink={cabinet.linkItem} href="#">
                <Button size="sm" className="rounded-full bg-primary text-xs normal-case">
                  Conoce más
                </Button>
              </CustomLink>
            ) : (
              <Button
                onClick={handleNavigation}
                size="sm"
                className="rounded-full bg-primary text-xs normal-case"
              >
                Conoce más
              </Button>
            )}
          </div>
        ) : null}

        {/* Sección de iconos - siempre al final */}
        <div className="mb-4 flex justify-center gap-x-4">
          {cabinet?.cabinetDetails?.map((item: CabinetDetail, key: any) => {
            return (
              <Fragment key={key}>
                {cabinetBlock?.pageBlockStyle != PageBlockStyles.B ? (
                  <CustomLink pageLink={item.cabinetDetailLink} href={`${item.cabinetDetailLink}`}>
                    <div className="rounded-full p-2">
                      <SvgBgPrimary
                        svgURL={`${item.cabinetDetailImage?.downloadUrl}`}
                        height={25}
                        width={25}
                      />
                    </div>
                  </CustomLink>
                ) : null}
              </Fragment>
            );
          })}
          {cabinet?.cabinetDetails?.length === 0 &&
          cabinetBlock?.pageBlockStyle != PageBlockStyles.B ? (
            <div className="flex justify-center gap-x-4">
              <TwitterIconX className="disabled-icon" />
              <InstagramIcon className="disabled-icon" />
              <FacebookIconX className="disabled-icon" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
